import { useCallback, useEffect, useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Card, Form, Space, Tabs } from 'antd';
import _intersectionWith from 'lodash.intersectionwith';
import { observer } from 'mobx-react-lite';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { CanAccess } from 'app/railways/domains/access/features/can-access';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { TPersonData } from 'app/railways/domains/passengers/core/person';
import {
  getPersonRequest,
  updatePersonRequest,
} from 'app/railways/domains/passengers/requests';

import { Loading } from '../../../../../ui';
import {
  CredentialFields,
  CredentialsFieldsPath,
} from '../add/credential-fields';
import {
  StatusDataFields,
  StatusDataFieldsPath,
} from '../add/status-data-fields';
import { ContactStatusFields } from './contact-data-fields';

const { TabPane } = Tabs;
// const TabPanePerm = withPerm(TabPane);

const ButtonSubmit = observer(() => {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button type="primary" htmlType="submit">
        Сохранить
      </Button>
    </div>
  );
});

type UpdatedPerson = Partial<TPersonData> & { id: number };

type PersonDetailsProps = {
  id: number;
};

const enum TabKeys {
  Common = '1',
  StatusData = '3',
}

const findErrorsInTab = (
  tabFields: Array<string[]>,
  errors: Array<string[]>,
): boolean => {
  const result = _intersectionWith(
    tabFields,
    errors,
    (fields, errors) => fields.join(',') === errors.join(','),
  );
  return result.length > 0;
};

const TabErrorTitle = observer(
  ({
    title,
    fields,
    errors,
  }: {
    title: string;
    fields: Array<string[]>;
    errors: Array<string[]>;
  }) => {
    return findErrorsInTab(fields, errors) ? (
      <span style={{ color: 'tomato' }}>
        <ExclamationCircleOutlined />
        {title}
      </span>
    ) : (
      <span>{title}</span>
    );
  },
);

export const PersonDetails = observer(({ id }: PersonDetailsProps) => {
  const authStatus = useAuthStatusWriter();
  const [form] = Form.useForm();
  const pageNotify = usePageNotify();

  const getPersonResource = useResource<TPersonData['id'], TPersonData>({
    request: getPersonRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatus.denyAccess(),
    onDone: (personData) => {
      form.setFieldsValue(personData);
    },
  });

  const updatePersonResource = useResource<UpdatedPerson, TPersonData>({
    request: updatePersonRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatus.denyAccess(),
    onDone(personData) {
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные пассажира обновлены',
      });
      form.setFieldsValue(personData);
    },
  });

  useEffect(() => {
    getPersonResource.send(id);
  }, []);

  const [currentTab, setCurrentTab] = useState('1');
  const [errorFields, setErrorFields] = useState<Array<string[]>>([]);

  const isLoading =
    getPersonResource.isLoading || updatePersonResource.isLoading;

  const handleUpdatePassanger = useCallback(
    (updatedPerson: UpdatedPerson) => {
      updatePersonResource.send({
        ...form.getFieldsValue(),
        ...updatedPerson,
        id,
      });

      setErrorFields([]);
    },
    [form, id, updatePersonResource],
  );

  const handleFormErrors = useCallback(
    (errors: ValidateErrorEntity<UpdatedPerson>) => {
      const errorsPathFields = errors.errorFields.map((field) => field.name);
      // @ts-expect-error ...
      setErrorFields(errorsPathFields);
    },
    [],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleUpdatePassanger}
      onFinishFailed={handleFormErrors}
    >
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Card size="small" bodyStyle={{ paddingTop: 0 }}>
          <Loading active={isLoading}>
            <Tabs activeKey={currentTab} onChange={setCurrentTab}>
              <TabPane
                forceRender
                tab={
                  <TabErrorTitle
                    title="Данные аккаунта"
                    fields={Object.values(CredentialsFieldsPath)}
                    errors={errorFields}
                  />
                }
                key={TabKeys.Common}
              >
                <CredentialFields />
              </TabPane>

              <TabPane
                forceRender
                tab={
                  <TabErrorTitle
                    title="Статус данных"
                    fields={Object.values(StatusDataFieldsPath)}
                    errors={errorFields}
                  />
                }
                key={TabKeys.StatusData}
              >
                <StatusDataFields />

                <ContactStatusFields />
              </TabPane>
            </Tabs>
          </Loading>
        </Card>

        <CanAccess perm="PERM_PERSON_UPDATE">
          <Card size="small">
            <ButtonSubmit />
          </Card>
        </CanAccess>
      </Space>
    </Form>
  );
});
